import { getPlanName } from '@/utils';
import {
    billingService, GetInvoicesResponse, GetUserPointsHistoryResponse,
    UserPlan, UserPoints
} from '@common/api/billingService';
import { useState } from 'react';

export interface UserBilling {
    userPlan: UserPlan | null;
    setUserPlan: (plan: UserPlan | null) => void;
    userPoints: UserPoints | null;
    pointsHistory: GetUserPointsHistoryResponse | null;
    invoices: GetInvoicesResponse | null;
    loading: {
        plan: boolean;
        points: boolean;
        history: boolean;
        invoices: boolean;
    };
    error: UserBillingError;
    fetchUserPlan: () => Promise<any>;
    fetchUserPoints: () => Promise<void>;
    fetchPointsHistory: (page?: number, size?: number) => Promise<void>;
    fetchInvoices: (page?: number, size?: number) => Promise<void>;
    refreshAll: () => Promise<void>;
    planText: {
        planDisplayText: string;
        buttonText: string;
        expirationText: string;
        pricingButtongText: string;
    };
}

export interface UserBillingError {
    plan: string | null;
    points: string | null;
    history: string | null;
    invoices: string | null;
}

export const useUserBilling = () => {
    const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
    const [userPoints, setUserPoints] = useState<UserPoints | null>(null);
    const [pointsHistory, setPointsHistory] = useState<GetUserPointsHistoryResponse | null>(null);
    const [invoices, setInvoices] = useState<GetInvoicesResponse | null>(null);
    const [loading, setLoading] = useState({
        plan: false,
        points: false,
        history: false,
        invoices: false
    });
    const [error, setError] = useState({
        plan: null,
        points: null,
        history: null,
        invoices: null
    });

    // Fetch user plan
    const fetchUserPlan = async () => {
        try {
            setLoading(prev => ({ ...prev, plan: true }));
            setError(prev => ({ ...prev, plan: null }));
            const response = await billingService.getUserPlans();
            setUserPlan(response);
            return response;
        } catch (err: any) {
            console.error('Failed to fetch user plan:', err);
            setError(prev => ({ ...prev, plan: err.message || 'Failed to fetch plan' }));
        } finally {
            setLoading(prev => ({ ...prev, plan: false }));
        }
    };

    // Fetch user points
    const fetchUserPoints = async () => {
        try {
            setLoading(prev => ({ ...prev, points: true }));
            setError(prev => ({ ...prev, points: null }));
            const response = await billingService.getUserPoints();
            setUserPoints(response);
        } catch (err: any) {
            console.error('Failed to fetch user points:', err);
            setError(prev => ({ ...prev, points: err.message || 'Failed to fetch points' }));
        } finally {
            setLoading(prev => ({ ...prev, points: false }));
        }
    };

    // Fetch points history
    const fetchPointsHistory = async (page: number = 1, size: number = 5) => {
        try {
            setLoading(prev => ({ ...prev, history: true }));
            setError(prev => ({ ...prev, history: null }));
            const response = await billingService.getUserPointsHistory({ page, size });
            
            if (page === 1) {
                setPointsHistory(response);
            } else {
                setPointsHistory(prev => {
                    if (!prev) return response;
                    return {
                        ...response,
                        data: [...prev.data, ...response.data]
                    };
                });
            }
        } catch (err: any) {
            console.error('Failed to fetch points history:', err);
            setError(prev => ({ ...prev, history: err.message || 'Failed to fetch history' }));
        } finally {
            setLoading(prev => ({ ...prev, history: false }));
        }
    };

    const fetchInvoices = async (page: number = 1, size: number = 5) => {
        try {
            setLoading(prev => ({ ...prev, invoices: true }));
            setError(prev => ({ ...prev, invoices: null }));
            const response = await billingService.getInvoices({ page, size });
            
            if (page === 1) {
                setInvoices(response);
            } else {
                setInvoices(prev => {
                    if (!prev) return response;
                    return {
                        ...response,
                        invoices: [...prev.invoices, ...response.invoices]
                    };
                });
            }
        } catch (err: any) {
            console.error('Failed to fetch invoices:', err);
            setError(prev => ({ ...prev, invoices: err.message || 'Failed to fetch invoices' }));
        } finally {
            setLoading(prev => ({ ...prev, invoices: false }));
        }
    }

    // Refresh all data
    const refreshAll = async (): Promise<void> => {
        await Promise.all([
            fetchUserPlan(),
            fetchUserPoints(),
            fetchPointsHistory(),
            fetchInvoices()
        ]);
    };

    const getPlanText = () =>{
        const isExpired = userPlan?.endAt && new Date().getTime() > new Date(userPlan?.endAt).getTime();
        const isCanceled = userPlan?.status === 'pending_cancellation';
        const planDisplayText = getPlanName(userPlan);
        let buttonText = '';
        let expirationText = '';
        let pricingButtongText = '';
        // Handle FREE plan
        if(userPlan?.planCode === 'FREE'){
            if(isExpired){
                buttonText= 'Upgrade',
                expirationText= 'Expired on',
                pricingButtongText= 'Upgrade'
            }else{
                buttonText= 'Upgrade',
                expirationText= 'Expires date',
                pricingButtongText= 'Upgrade'
            }
            return {
                planDisplayText,
                buttonText,
                expirationText,
                pricingButtongText
            }
        }
        // handle other plans
        if(isExpired){  // 订阅过期取消订阅
            buttonText= 'Renew',
            expirationText= 'Expired on',
            pricingButtongText= 'Renew'
        }else if(isCanceled){ // 取消自动续费
            buttonText= 'Manage',
            expirationText= 'Expires date',
            pricingButtongText= 'Current Plan'
        }else{ // 自动续费
            buttonText= 'Manage',
            expirationText= 'Will renew on',
            pricingButtongText= 'Current Plan'
        }
        return {
            planDisplayText,
            buttonText,
            expirationText,
            pricingButtongText
        }
    }

    return {
        userPlan,
        setUserPlan,
        userPoints,
        pointsHistory,
        invoices,
        loading,
        error,
        fetchUserPlan,
        fetchUserPoints,
        fetchPointsHistory,
        fetchInvoices,
        refreshAll,
        planText: getPlanText()
    };
};
